import React from 'react';

import { css, cx } from '@emotion/css';
import { Typography } from '@mui/material';
import { useFormState, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useStyles } from '@/hooks/useStyles.ts';
import { CarePlanDirectives } from '@/models/CarePlanDirectivesModel.ts';
import { TelemonitoringTier } from '@/models/CarePlanModel.ts';
import { MedicationSchema } from '@/models/ConditionModel.ts';
import { Theme } from '@/theme/CreateTheme';
import { InformationPaper } from '@/uiKit/atoms/InformationPaper.tsx';
import { TelemonitoringCriteriaPick } from '@/uiKit/molecules/TelemonitoringCriteriaPick.tsx';
import { TelemonitoringTierChip } from '@/uiKit/molecules/TelemonitoringTierChip.tsx';
import { SimpleCarePlanCreationInfo } from '@/uiKit/organisms/PatientCreationAndRenewal/CarePlan/CarePlanCreation.ts';

export type TelemonitoringCriteriaSelectionFieldProps = {
  className?: string;
  carePlanDirectives: CarePlanDirectives;
  telemonitoringTierRestrictions?: TelemonitoringTier;
};

export const TelemonitoringCriteriaSelectionField: React.FC<
  TelemonitoringCriteriaSelectionFieldProps
> = ({ telemonitoringTierRestrictions, carePlanDirectives, className }) => {
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();
  const insulinScheme = useWatch({
    name: 'insulinScheme',
  }) as MedicationSchema | undefined;
  const criteriaError = useFormState<SimpleCarePlanCreationInfo>({
    name: 'inclusionCriteria',
  }).errors.inclusionCriteria;
  // No Care Plan creation if there is no insulin
  if (insulinScheme === 'noInsulin') {
    return null;
  }

  return (
    <div className={cx(styles.container, className)}>
      <Typography variant="subtitle">{t('diabetesForms.criteria')}</Typography>
      {telemonitoringTierRestrictions ? (
        <div>
          <InformationPaper>{t('diabetesForms.restriction')}</InformationPaper>
        </div>
      ) : null}
      <div>
        <div className={styles.sideBySide}>
          <TelemonitoringCriteriaPick carePlanDirectives={carePlanDirectives} />
          <TelemonitoringTierChip carePlanDirectives={carePlanDirectives} />
        </div>
        {criteriaError?.message ? (
          <Typography color="error" variant="caption">
            {t(criteriaError.message)}
          </Typography>
        ) : null}
      </div>
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(12),
    paddingBottom: theme.spacing(12),
  }),
  sideBySide: css({
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(10),

    width: '100%',
    alignItems: 'center',
  }),
});
